import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useOutletContext } from 'react-router-dom';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { Section } from '~/components';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

import { CardAccountProps } from './CardAccount';

const columns: TableColumn<Card>[] = [
  {
    header: 'Authorized User',
    accessor: 'authorizedUserEntityId',
    width: { min: 140, max: '2fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'BIN',
    accessor: 'bin',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'Last 4',
    accessor: 'lastFourDigits',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'Expires',
    accessor: 'expiresAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value) : '-'} />,
  },
  {
    header: 'Type',
    accessor: 'type',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value) : '-'} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => (value ? <RelativeTime timestamp={value as Date} /> : <Table.TextCell value="-" />),
  },
];

export const PageCardAccountCards: React.FC = () => {
  const navigate = useNavigate();
  const { cardAccount } = useOutletContext<CardAccountProps>();
  const { currentPlatform, isSandbox } = useSessionStore();

  const { data: cardsData, isLoading } = useQuery({
    queryKey: ['card-account-cards', cardAccount?.id, currentPlatform?.id, isSandbox],
    queryFn: () => CardRepository.listCards({ cardAccountId: cardAccount.id }),
    enabled: !!cardAccount?.id,
  });

  return (
    <Inner p={0}>
      <Section headline="Cards">
        <Inner px="var(--page-margin)" py={0}>
          <Table<Card>
            columns={columns}
            data={cardsData?.cards ?? []}
            isLoading={isLoading}
            variant={TableVariant.List}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARDS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Section>
    </Inner>
  );
};
